/* The browser's half of the access gate.
 *
 * What is kept here is the token the server hands back, never the password:
 * the password is typed once, posted, and forgotten. The token is only worth
 * anything against a server that knows the same password, so losing it costs
 * whoever holds it nothing more than the password already would.
 *
 * localStorage rather than a cookie, because the gate is a front-end concern -
 * the API reads it from a header, so there is nothing for a browser to attach
 * to a cross-site request on its own.
 */

const KEY = "hta.token";

/**
 * Two stores, picked by "Keep me signed in": localStorage outlives the
 * browser, sessionStorage dies with the tab. Reads check both, so neither
 * choice needs remembering anywhere else.
 *
 * Reading can throw in a locked-down browser; an unreadable store is no token.
 */
export function readToken(): string | null {
  try {
    return localStorage.getItem(KEY) ?? sessionStorage.getItem(KEY);
  } catch {
    return null;
  }
}

export function saveToken(token: string, persist = true): void {
  try {
    /* Clear the other store first, or an old token left in it would outlive
       the one being saved and decide the next visit. */
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
    (persist ? localStorage : sessionStorage).setItem(KEY, token);
  } catch {
    /* Private mode and the like. The session still works, it just will not
       outlive the tab - which is better than refusing to sign in at all. */
  }
}

export function clearToken(): void {
  try {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
  } catch { /* nothing to clear if the store is unreadable */ }
}

/* --------------------------- Microsoft 365 sign-in ---------------------------
 * That door leaves no token here. The server sets an httpOnly session cookie,
 * which the browser attaches to same-origin requests by itself, so there is
 * nothing for this module to store or read back - only to ask about.
 */

export interface SessionUser {
  sub: string;
  name: string;
  email: string;
}

/** Whether the server can offer the Microsoft button at all. */
export async function ssoEnabled(): Promise<boolean> {
  try {
    const res = await fetch("/api/auth/config");
    if (!res.ok) return false;
    return !!((await res.json()) as { ssoEnabled?: boolean }).ssoEnabled;
  } catch {
    return false;
  }
}

/** The signed-in Microsoft user, or null when there is no session cookie. */
export async function currentUser(): Promise<SessionUser | null> {
  try {
    const res = await fetch("/api/auth/me");
    if (!res.ok) return null;
    return (await res.json()) as SessionUser;
  } catch {
    return null;
  }
}

/** Hand the browser to Microsoft; it comes back to "/" with a session. */
export function startSso(): void {
  window.location.href = "/api/auth/login";
}

/**
 * Drop both doors at once: the stored password token and the session cookie.
 * Signing out of one while the other still opens the gate would look broken.
 */
export async function signOutEverywhere(): Promise<void> {
  clearToken();
  try {
    await fetch("/api/auth/logout", { method: "POST" });
  } catch { /* already gone, or offline - the reload still lands on the gate */ }
}

/**
 * Exchange the password for a token. Throws with the server's own message on
 * a wrong password, so the form can show it as-is.
 */
export async function login(password: string, persist = true): Promise<void> {
  const res = await fetch("/api/login", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body: JSON.stringify({ password }),
  });
  if (!res.ok) {
    let detail = "Could not sign in.";
    try {
      const body = (await res.json()) as { error?: string };
      if (body.error) detail = body.error;
    } catch { /* non-JSON error body */ }
    throw new Error(detail);
  }
  const { token } = (await res.json()) as { token: string };
  saveToken(token, persist);
}
